import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Button, Row, Col } from 'reactstrap';
import '../css/ProductList.css';
import IconInput from '../components/Shared/IconInput';
import { Search } from 'react-bootstrap-icons';
import { Link, useLocation } from 'react-router-dom';
import ProdView from '../components/ProdView';
import { ArrowLeft } from 'react-bootstrap-icons';
import fallbackImage from '../assets/fallback.jpg';

const Products = () => {
    const location = useLocation();
    const state = location?.state || {};
    const { cat = [], products = [], selCat = 0 } = state;

    const [loading, setLoading] = useState(true);
    const [content, setContent] = useState([]);
    const [filterBy, setFilterBy] = useState(0);
    const [filterList, setFilterList] = useState([]);
    const [showProductList, setShowProductList] = useState(true);
    const [selectedProdID, setSelectedProdID] = useState(null);
    const [imageDirectory, setImageDirectory] = useState('');
    const [documents, setDocuments] = useState(null);
    const [categories, setCategories] = useState(cat);
    const [productList, setProductList] = useState(products);

    const bearerToken = localStorage.getItem('bearerToken');

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15;

    // Helper function to check if product belongs to a category
    const productBelongsToCategory = (product, categoryId) => {
        if (categoryId === 0) return true;
        const productCategories = String(product.caT_ID || '').split(',').map(c => c.trim());
        return productCategories.includes(String(categoryId));
    };

    useEffect(() => {
        const initializeData = async () => {
            setLoading(true);

            try {
                // If products weren't passed via state, fetch them
                if (!Array.isArray(products) || products.length === 0) {
                    console.log('No products in state, fetching from API...');
                    await fetchProducts();
                    await fetchCategories();
                } else {
                    console.log("Products received from state:", products);
                    setProductList(products);
                    setCategories(cat);
                }

                await getDirectories();

            } catch (error) {
                console.error('Error initializing data:', error);
            } finally {
                setLoading(false);
            }
        };

        initializeData();
    }, []);

    useEffect(() => {
        if (productList.length > 0) {
            preSetCategory();
        }
    }, [productList, selCat]);

    const fetchProducts = async () => {
        try {
            const response = await axios.get('/Website/getProducts', {
                headers: { Authorization: `Bearer ${bearerToken}` }
            });
            console.log('Fetched products:', response.data);
            setProductList(response.data || []);
        } catch (error) {
            console.error('Error fetching products:', error);
            setProductList([]);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get('/Website/GetCategories', {
                headers: { Authorization: `Bearer ${bearerToken}` }
            });
            console.log('Fetched categories:', response.data);
            setCategories(response.data || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
            setCategories([]);
        }
    };

    const getDirectories = async () => {
        try {
            const response = await axios.get('/Website/GetDirectories', {
                headers: { Authorization: `Bearer ${bearerToken}` }
            });
            setImageDirectory(response.data?.imgDirectory || '');
            setDocuments(response.data?.docsDirectory || '');
        } catch (error) {
            console.error('Error fetching image directory:', error);
        }
    };

    const preSetCategory = () => {
        const catID = parseInt(selCat, 10) || 0;
        setFilterBy(catID);

        let filteredList = Array.isArray(productList) ? [...productList] : [];

        if (catID !== 0) {
            filteredList = filteredList.filter(item =>
                productBelongsToCategory(item, catID)
            );
        }

        setContent(filteredList);
    };

    const handleCategoryChange = (e) => {
        const catID = parseInt(e.target.value, 10) || 0;
        setFilterBy(catID);
        setCurrentPage(1);

        let filteredList = Array.isArray(productList) ? [...productList] : [];

        if (catID !== 0) {
            filteredList = filteredList.filter(item =>
                productBelongsToCategory(item, catID)
            );
        }

        setContent(filteredList);
    };

    const searchProducts = (text) => {
        setCurrentPage(1);

        const filteredByText = Array.isArray(productList)
            ? productList.filter(item =>
                item.proD_NAME?.toLowerCase().includes(text.toLowerCase())
            )
            : [];

        const result = filterBy === 0
            ? filteredByText
            : filteredByText.filter(item =>
                productBelongsToCategory(item, filterBy)
            );

        setFilterList(result);
        setContent(result);
    };

    const totalPages = Math.ceil(content.length / itemsPerPage);
    const paginatedProducts = content.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const changePage = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
            window.scrollTo(0, 0);
        }
    };

    if (loading) {
        return (
            <Container style={{ textAlign: 'center', paddingTop: '10%' }}>
                <span className="loader"></span>
            </Container>
        );
    }

    if (!Array.isArray(productList) || productList.length === 0) {
        return (
            <Container style={{ paddingTop: '5%' }}>
                <p>No products available.</p>
            </Container>
        );
    }

    return (
        <div>
            {showProductList ? (
                <>
                    <Row className="align-items-center py-4 mb-4">
                        <Col md="4" className="mb-3 mb-md-0">
                            <IconInput
                                placeholder="Search"
                                icon={<Search />}
                                name="NavSearchBar"
                                size="lg"
                                type="text"
                                autoComplete="search"
                                onChange={(e) => searchProducts(e.target.value)}
                                style={{ width: '100%' }}
                            />
                        </Col>

                        <Col md="4" className="mb-3 mb-md-0">
                            <select
                                className="form-select"
                                value={filterBy}
                                onChange={handleCategoryChange}
                            >
                                <option value="0">All Categories</option>
                                {Array.isArray(categories) && categories.map(c => (
                                    <option key={c.caT_ID} value={c.caT_ID}>
                                        {c.caT_NAME}
                                    </option>
                                ))}
                            </select>
                        </Col>

                        <Col md="4" className="d-flex justify-content-md-end justify-content-start">
                            <span className="me-2 align-self-center">
                                Page {currentPage} of {totalPages}
                            </span>
                            <div className="d-flex gap-2">
                                {[...Array(totalPages)].map((_, i) => {
                                    const isActive = currentPage === i + 1;
                                    return (
                                        <Button
                                            key={i}
                                            onClick={() => changePage(i + 1)}
                                            className="rounded-1"
                                            style={{
                                                backgroundColor: isActive ? '#ac130a' : '#000000',
                                                color: '#ffffff',
                                                border: 'none'
                                            }}
                                        >
                                            {i + 1}
                                        </Button>
                                    );
                                })}
                            </div>
                        </Col>
                    </Row>

                    <div className="product-grid">
                        {paginatedProducts.map((product, index) => (
                            <div key={index} className="card-col">
                                <div
                                    style={{
                                        border: '1px solid black',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                        padding: '10px',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Link to={`/ProdView/${product.proD_NAME}`}
                                        state={{
                                            imgDirectory: imageDirectory,
                                            docsDirectory: documents,
                                            prod: product
                                        }}
                                        style={{
                                            color: 'black',
                                            textDecoration: 'none',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            height: '100%',
                                            justifyContent: 'space-between',
                                            padding: '5px',
                                        }}
                                    >
                                        {product.imG_PATH && imageDirectory && (
                                            <div className="image-container">
                                                <img
                                                    src={product.imG_PATH ? `${imageDirectory}/${product.imG_PATH}` : fallbackImage}
                                                    alt={product.proD_NAME || 'Product'}
                                                    style={{ objectFit: 'contain', width: '100%', height: '100%' }}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = fallbackImage;
                                                        e.target.style.objectFit = 'contain';
                                                    }}
                                                />
                                            </div>
                                        )}
                                        <div style={{ flexGrow: 0, textAlign: 'center', marginTop: 'auto' }}>
                                            <h5 style={{ margin: 0 }}>{product.proD_NAME}</h5>
                                        </div>
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <div>
                    <Button
                        color="link"
                        className="back-button"
                        onClick={() => {
                            setSelectedProdID(null);
                            setShowProductList(true);
                        }}
                    >
                        <ArrowLeft className="back-icon" size={20} color="black" />
                        <span className="back-text">Back to Products</span>
                    </Button>
                    <ProdView prod={selectedProdID} />
                </div>
            )}
        </div>
    );
};

export default Products;